import { useState, useCallback, useMemo } from "react";
import WidgetRenderer from "./WidgetRenderer";
import WorldSectionRenderer from "./WorldSectionRenderer";
import { killAllAudio } from "../widgets/AudioPlayButton.jsx";
import { getBrand } from "../branding";

const brand = getBrand();

/* ───────── Spinner ───────── */

function Spinner({ size = 14 }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        border: "2px solid rgba(255,255,255,0.4)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        display: "inline-block",
        animation: "spin 0.8s linear infinite"
      }}
    />
  );
}

/* keyframes once */
const spinnerStyle = (
  <style>
    {`
      @keyframes spin { to { transform: rotate(360deg); } }

      .exam-submit-button {
        width: min(100%, 440px);
        min-height: 56px;
        padding: 14px 24px;
        border: none;
        border-radius: 10px;
        background: #2e7d32;
        color: #fff;
        font-size: 19px;
        font-weight: 800;
        letter-spacing: 0.1px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        box-shadow: 0 4px 12px rgba(46, 125, 50, 0.24);
        transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
      }

      .exam-submit-button:hover {
        background: #256b29;
        box-shadow: 0 6px 16px rgba(46, 125, 50, 0.3);
        transform: translateY(-1px);
      }

      .exam-submit-button:active {
        transform: translateY(0);
        box-shadow: 0 3px 8px rgba(46, 125, 50, 0.22);
      }

      .exam-submit-button:focus-visible {
        outline: 3px solid rgba(46, 125, 50, 0.28);
        outline-offset: 3px;
      }

      @media (max-width: 600px) {
        .exam-submit-button {
          width: 100%;
          font-size: 18px;
        }
      }
    `}
  </style>
);

export default function ExamPage({
  exam,
  initialAnswers = {},
  initialFeedback = null,
  initialMode = "edit",
  practiceMode = false,
  chairMode = false,
  readOnly = false,
  devMode = false,
}) {
  const [answers, setAnswers] = useState(initialAnswers);
  const [feedback, setFeedback] = useState(initialFeedback);
  const [mode, setMode] = useState(initialMode);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scoreSummary, setScoreSummary] = useState(null);
  const [showPracticeCta, setShowPracticeCta] = useState(false);

  /* ───────── Stable updater ───────── */

  const updateAnswer = useCallback((id, val) => {
    if (readOnly) return;
    setAnswers(prev => ({ ...prev, [id]: val }));
  }, [readOnly]);

  /* ───────── Prebind handlers ONCE ───────── */

  const changeHandlers = useMemo(() => {
    const map = {};
    exam.questions.forEach(q => {
      map[q.id] = val => updateAnswer(q.id, val);
    });
    return map;
  }, [exam.questions, updateAnswer]);

  /* ───────── Helpers ───────── */

  function questionLabel(q) {
    return q?.config?.number ? `Q${q.config.number}` : "Q?";
  }

  // function findUnanswered(exam, answers) {
  //   const missing = [];

  //   exam.questions.forEach(q => {
  //     if (q.type === "section-divider") return;

  //     const base = questionLabel(q);
  //     const val = answers[q.id];

  //     if (val == null) {
  //       missing.push(base);
  //       return;
  //     }

  //     if (Array.isArray(val)) {
  //       val.forEach((v, i) => {
  //         if (v == null || v === "") {
  //           missing.push(`${base}${String.fromCharCode(65 + i)}`);
  //         }
  //       });
  //       return;
  //     }

  //     if (typeof val === "object") {
  //       const keys = Object.keys(val).filter(k => k !== "_audio");
  //       if (keys.length === 0) {
  //         missing.push(base);
  //         return;
  //       }

  //       keys.forEach((k, i) => {
  //         if (val[k] == null || val[k] === "") {
  //           missing.push(`${base}${String.fromCharCode(65 + i)}`);
  //         }
  //       });
  //     }
  //   });

  //   return missing;
  // }
  // function findUnanswered(exam, answers) {
  //   const missing = [];

  //   function checkQuestion(q, val, ctx = {}) {
  //     const { sectionId, sectionIndex } = ctx;

  //     /* ── LABEL BUILDER ───────────────────────── */
  //     function labelFor(partIndex) {
  //       if (sectionId) {
  //         const suffix =
  //           partIndex != null
  //             ? `.${String.fromCharCode(97 + partIndex)}`
  //             : "";
  //         return `${sectionId}-${sectionIndex}${suffix}`;
  //       }

  //       return q?.config?.number
  //         ? `Q${q.config.number}`
  //         : q.id;
  //     }

  //     /* ── WORLD SECTION ───────────────────────── */
  //     if (q.type === "world-section") {
  //       const sectionAnswers = val || {};
  //       const sectionQuestions = q.config?.questions || [];

  //       sectionQuestions.forEach((sq, i) => {
  //         checkQuestion(
  //           sq,
  //           sectionAnswers[sq.id],
  //           {
  //             sectionId: q.id,
  //             sectionIndex: i + 1
  //           }
  //         );
  //       });
  //       return;
  //     }

  //     /* ── WORLD-SECTION MATCHING ───────────────── */
  //     if (q.type === "matching" && Array.isArray(q.blanks)) {
  //       q.blanks.forEach((b, i) => {
  //         const v = val?.[b.id];
  //         if (v == null || v === "") {
  //           missing.push(labelFor(i));
  //         }
  //       });
  //       return;
  //     }

  //     /* ── MULTI-PART ──────────────────────────── */
  //     if (q.type === "multi-part" && Array.isArray(q.parts)) {
  //       q.parts.forEach((_, i) => {
  //         const v = val?.[i];
  //         if (v == null || v === "") {
  //           missing.push(labelFor(i));
  //         }
  //       });
  //       return;
  //     }

  //     /* ── NO ANSWER AT ALL ─────────────────────── */
  //     if (val == null) {
  //       missing.push(labelFor());
  //       return;
  //     }

  //     /* ── ARRAY ANSWERS ───────────────────────── */
  //     if (Array.isArray(val)) {
  //       val.forEach((v, i) => {
  //         if (v == null || v === "") {
  //           missing.push(labelFor(i));
  //         }
  //       });
  //       return;
  //     }

  //     /* ── OBJECT ANSWERS ──────────────────────── */
  //     if (typeof val === "object") {
  //       const keys = Object.keys(val).filter(k => k !== "_audio");

  //       if (keys.length === 0) {
  //         missing.push(labelFor());
  //         return;
  //       }

  //       keys.forEach((k, i) => {
  //         if (val[k] == null || val[k] === "") {
  //           missing.push(labelFor(i));
  //         }
  //       });
  //     }
  //   }

  //   exam.questions.forEach(q => {
  //     if (q.type === "section-divider") return;
  //     checkQuestion(q, answers[q.id]);
  //   });

  //   return missing;
  // }
  function findUnanswered(exam, answers) {
    const missing = [];

    function checkQuestion(q, val, ctx = {}) {
      const { sectionId, sectionIndex } = ctx;

      /* ── LABEL BUILDER ───────────────────────── */
      function labelFor(partIndex) {
        if (sectionId) {
          const suffix =
            partIndex != null
              ? `.${String.fromCharCode(97 + partIndex)}`
              : "";
          return `${sectionId}-${sectionIndex}${suffix}`;
        }

        return q?.config?.number
          ? `Q${q.config.number}`
          : q.id;
      }

      /* ── WORLD SECTION ───────────────────────── */
      if (q.type === "world-section") {
        const sectionAnswers = val || {};
        const sectionQuestions = q.config?.questions || [];

        sectionQuestions.forEach((sq, i) => {
          checkQuestion(
            sq,
            sectionAnswers[sq.id],
            { sectionId: q.id, sectionIndex: i + 1 }
          );
        });
        return;
      }

      /* ── MATCHING / MATCHING-STACKED ─────────── */
      if (
        (q.type === "matching" || q.type === "matching-stacked") &&
        Array.isArray(q.blanks)
      ) {
        q.blanks.forEach((b, i) => {
          if (!val || val[b.id] == null || val[b.id] === "") {
            missing.push(labelFor(i));
          }
        });
        return;
      }

      /* ── LETTER INPUT ───────────────────────── */
      if (q.type === "letter-input" && Array.isArray(q.items)) {
        q.items.forEach((item, i) => {
          if (!item.id) return; // skip headings
          if (!val || val[item.id] == null || val[item.id] === "") {
            missing.push(labelFor(i));
          }
        });
        return;
      }

      /* ── FILL IN BLANK ───────────────────────── */
      if (q.type === "fill-in-blank" && Array.isArray(q.questions)) {
        q.questions.forEach((qq, i) => {
          if (!val || val[qq.id] == null || val[qq.id] === "") {
            missing.push(labelFor(i));
          }
        });
        return;
      }

      /* ── ERA COMPOSER ────────────────────────── */
      // if (q.type === "era-composer" && Array.isArray(q.composers)) {
      //   q.composers.forEach((_, i) => {
      //     if (!val || val[i] == null || val[i] === "") {
      //       missing.push(labelFor(i));
      //     }
      //   });
      //   return;
      // }
      /* ── ERA COMPOSER (drag/drop version) ───────────────── */
      if (q.type === "era-composer") {
        const parts = ["part1", "part2", "part3", "part4"];

        parts.forEach((pid, i) => {
          const part = val?.[pid];

          // Check era
          if (!part || !part.era) {
            missing.push(labelFor(i));
            return;
          }

          // Check composers
          if (!Array.isArray(part.composers)) {
            missing.push(labelFor(i));
            return;
          }

          const hasEmptyComposer = part.composers.some(
            c => !c || c === ""
          );

          if (hasEmptyComposer) {
            missing.push(labelFor(i));
          }
        });

        return;
      }

      /* ── ANALYSIS INVERSE ────────────────────── */
      if (q.type === "analysis-inverse" && Array.isArray(q.pieces)) {
        q.pieces.forEach((p, i) => {
          if (!val || val[p.id] == null || val[p.id] === "") {
            missing.push(labelFor(i));
          }
        });
        return;
      }

      /* ── MULTIPLE CHOICE ─────────────────────── */
      if (q.type === "multiple-choice" && Array.isArray(q.items)) {
        q.items.forEach((item, i) => {
          if (!val || val[item.id] == null || val[item.id] === "") {
            missing.push(labelFor(i));
          }
        });
        return;
      }

      /* ── MULTI-PART ──────────────────────────── */
      if (q.type === "multi-part" && Array.isArray(q.parts)) {
        q.parts.forEach((_, i) => {
          if (!val || val[i] == null || val[i] === "") {
            missing.push(labelFor(i));
          }
        });
        return;
      }

      /* ── MEASURE CLICK ───────────────────────── */
      if (q.type === "measure-click") {
        if (!val || Object.keys(val).length === 0) {
          missing.push(labelFor());
        }
        return;
      }

      /* ── TEXT IMAGE CHOICE ───────────────────── */
      if (q.type === "text-image-choice") {
        if (!val) missing.push(labelFor());
        return;
      }

      /* ── CIRCLE TEXT / STIMULUS CIRCLE TEXT ──── */
      if (
        q.type === "circle-text" ||
        q.type === "stimulus-circle-text"
      ) {
        if (!val || Object.keys(val).length === 0) {
          missing.push(labelFor());
        }
        return;
      }

      /* ── RHYTHM LINE COMPOSE + BEAMING ───────── */
      if (q.type === "rhythm-line-compose-beaming") {
        const count = Math.max(1, Number(q.config?.measures) || 1);
        const includeCounts = q.config?.includeCounts === true;
        const measures = Array.isArray(val?.rhythm?.measures) ? val.rhythm.measures : [];
        const hasMissing = Array.from({ length: count }, (_, index) => {
          const rhythms = Array.isArray(measures[index]?.rhythms) ? measures[index].rhythms : [];
          if (rhythms.length === 0) return true;
          if (!includeCounts) return false;
          const counts = Array.isArray(measures[index]?.counts) ? measures[index].counts : [];
          return rhythms.some((_, rhythmIndex) => String(counts[rhythmIndex] ?? "").trim() === "");
        }).some(Boolean);
        if (hasMissing) missing.push(labelFor());
        return;
      }

      /* ── FALLBACK (single-value question) ────── */
      if (val == null || val === "") {
        missing.push(labelFor());
      }
    }

    exam.questions.forEach(q => {
      if (q.type === "section-divider") return;
      checkQuestion(q, answers[q.id]);
    });

    return missing;
  }


  /* ───────── Submit logic ───────── */

  function submitExam() {
    setPendingSubmit(findUnanswered(exam, answers));
  }

  async function submitExamConfirmed() {
    if (isSubmitting) return;

    setIsSubmitting(true);
    killAllAudio();

    try {
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          assessmentId: exam.id,
          answers,
          practiceMode,
          chairMode,
          devMode
        })
      });

      const grading = await res.json();

      setPendingSubmit(null);

      const gradingSummary = grading?.grading ?? grading;
      const feedbackResults = gradingSummary?.results ?? null;

      if (practiceMode && gradingSummary) {
        const total = Number(gradingSummary.total);
        const max = Number(gradingSummary.max);
        const rawPercent = Number(gradingSummary.percent);

        setScoreSummary({
          total: Number.isFinite(total) ? total : null,
          max: Number.isFinite(max) ? max : null,
          percent: Number.isFinite(rawPercent)
            ? rawPercent
            : Number.isFinite(total) && Number.isFinite(max) && max > 0
              ? total / max
              : null
        });
        setShowPracticeCta(true);
      }

      if (((practiceMode || chairMode) && feedbackResults) || devMode) {
        setFeedback(feedbackResults);
        setMode("review");
      }
      else {
        // ───── REAL EXAM: lock and exit ─────
        setSubmitted(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  /* ───────── Render ───────── */

  if (submitted && initialMode === "edit") {
    return (
      <div style={{ padding: "40px", width: "100vw", margin: "0 auto", textAlign: "center" }}>
        <h2>✅ Exam Submitted</h2>
        <p>Your responses have been successfully recorded.</p>
        <p>You may now close this window.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "40px",
        width: "100%",
        // maxWidth: "1200px",
        minWidth: 0,
        margin: "0 auto",
        boxSizing: "border-box"
      }}
    >
      {spinnerStyle}
      {(chairMode || devMode) && (
        <ModeBanner chairMode={chairMode} devMode={devMode}/>
      )}

      {practiceMode && <PracticePromoBanner />}

      {practiceMode && scoreSummary && (
        <PracticeScoreSummary score={scoreSummary} />
      )}

      <h1>{exam.title}</h1>

      {exam.questions.map(q => {
        if (q.type === "world-section") {
          return (
            <WorldSectionRenderer
              key={q.id}
              section={q}
              value={answers[q.id] || {}}
              onChange={changeHandlers[q.id]}
              mode={mode}
              feedback={feedback?.[q.id]}
            />
          );
        }

        if (q.type === "section-divider") {
          return (
            <WidgetRenderer
              key={q.id}
              question={q}
              mode={mode}
            />
          );
        }

        return (
          <div
            key={q.id}
            style={{
              marginBottom: "30px",
              padding: "20px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box"
            }}
          >
            {q.prompt && (
              <p style={{ fontSize: "18px" }}>
                <strong>{q.prompt}</strong>
              </p>
            )}

            <WidgetRenderer
              question={q}
              value={answers[q.id]}
              onChange={changeHandlers[q.id]}
              mode={mode}
              feedback={feedback?.[q.id]}
            />
          </div>
        );
      })}

      {mode === "edit" && (
        <div
          style={{
            marginTop: 32,
            marginBottom: 8,
            display: "flex",
            justifyContent: "center"
          }}
        >
          <button
            type="button"
            className="exam-submit-button"
            onClick={submitExam}
          >
            {/* <span aria-hidden="true" style={{ fontSize: 21, lineHeight: 1 }}>✓</span> */}
            <span>Submit Exam</span>
          </button>
        </div>
      )}

      {mode === "review" && (
        <div style={{ marginTop: "20px" }}>
          <div style={{ fontSize: "18px" }}>
            <strong>Review your answers above.</strong>
          </div>
        </div>
      )}

      {pendingSubmit && (
        <ConfirmSubmitModal
          missing={pendingSubmit}
          submitting={isSubmitting}
          onCancel={() => !isSubmitting && setPendingSubmit(null)}
          onConfirm={submitExamConfirmed}
        />
      )}

      {practiceMode && showPracticeCta && scoreSummary && (
        <PracticeCtaModal
          score={scoreSummary}
          onClose={() => setShowPracticeCta(false)}
          onReview={() => {
            setShowPracticeCta(false);
            requestAnimationFrame(() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            });
          }}
        />
      )}
    </div>
  );
}

function formatScoreValue(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;

  if (Math.abs(numeric) < 1e-9) return "0";

  // Raw scores may contain legitimate fractional credit. Keep the display
  // readable without exposing floating-point artifacts.
  const rounded = Math.round((numeric + Number.EPSILON) * 100) / 100;
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

function PracticePromoBanner() {
  return (
    <div
      style={{
        marginBottom: 24,
        padding: "16px 18px",
        border: "1px solid #d9e2f2",
        borderRadius: 10,
        background: "#f8fbff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 18,
        flexWrap: "wrap"
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          This free practice test is provided by Lion Theory.
        </div>
        <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.45 }}>
          Want more practice for {brand.shortName} theory exams? Lion Theory offers guided practice by level.
        </div>
      </div>
      <a
        href="https://liontheory.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "9px 14px",
          borderRadius: 7,
          background: "#1f5ea8",
          color: "#fff",
          textDecoration: "none",
          fontWeight: 700,
          fontSize: 14,
          whiteSpace: "nowrap"
        }}
      >
        Learn more
      </a>
    </div>
  );
}

function PracticeScoreSummary({ score }) {
  const total = formatScoreValue(score.total);
  const max = formatScoreValue(score.max);

  return (
    <div
      style={{
        padding: "18px 20px",
        borderRadius: 10,
        background: "#f3f8f4",
        border: "1px solid #cfe2d2",
        fontSize: 18
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5, color: "#47624b" }}>
        PRACTICE TEST SCORE
      </div>
      <div style={{ marginTop: 5, fontSize: 26, fontWeight: 800 }}>
        {total != null && max != null
          ? `${total} / ${max}`
          : "Score complete"}
      </div>
    </div>
  );
}

function PracticeCtaModal({ score, onClose, onReview }) {
  const total = formatScoreValue(score.total);
  const max = formatScoreValue(score.max);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.48)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 1100
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="practice-cta-title"
    >
      <div
        style={{
          width: 480,
          maxWidth: "100%",
          background: "#fff",
          borderRadius: 12,
          padding: 28,
          boxShadow: "0 18px 50px rgba(0,0,0,0.28)",
          textAlign: "center"
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.6, color: "#59705d" }}>
          YOUR PRACTICE TEST SCORE
        </div>
        <div style={{ marginTop: 6, fontSize: 32, fontWeight: 800 }}>
          {total != null && max != null
            ? `${total} / ${max}`
            : "Complete"}
        </div>

        <h2 id="practice-cta-title" style={{ margin: "24px 0 8px" }}>
          Ready to practice more?
        </h2>
        <p style={{ margin: "0 auto", maxWidth: 390, color: "#4b5563", lineHeight: 1.55 }}>
          Build your skills with Lion Theory practice designed for {brand.shortName} theory exam levels.
        </p>

        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "center",
            gap: 10,
            flexWrap: "wrap"
          }}
        >
          <button
            type="button"
            onClick={onReview}
            style={{
              padding: "10px 16px",
              borderRadius: 7,
              border: "1px solid #c8cdd3",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 600
            }}
          >
            Review my answers
          </button>
          <a
            href="https://liontheory.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px 16px",
              borderRadius: 7,
              background: "#1f5ea8",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 700
            }}
          >
            Visit Lion Theory
          </a>
        </div>
      </div>
    </div>
  );
}

/* ───────── Modal ───────── */

function ConfirmSubmitModal({ missing, onCancel, onConfirm, submitting }) {
  const hasMissing = missing.length > 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: 24,
          borderRadius: 10,
          width: 420,
          maxWidth: "90%",
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)"
        }}
      >
        <h2 style={{ marginTop: 0 }}>
          {hasMissing ? "Unanswered Questions" : "Submit Exam?"}
        </h2>

        {hasMissing ? (
          <>
            <p>The following items are unanswered:</p>
            <div
              style={{
                background: "#f5f5f5",
                padding: 12,
                borderRadius: 6,
                marginBottom: 16,
                maxHeight: 160,
                overflowY: "auto",
                fontFamily: "monospace",
                fontSize: 14
              }}
            >
              {missing.join(", ")}
            </div>
            <p>
              You may submit anyway, but unanswered items will receive no credit.
            </p>
          </>
        ) : (
          <p style={{ marginBottom: 24 }}>
            You have answered all questions.  
            Are you ready to submit your exam?
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button onClick={onCancel} disabled={submitting}>
            Go Back
          </button>

          <button
            onClick={onConfirm}
            disabled={submitting}
            style={{
              background: hasMissing ? "#c62828" : "#2e7d32",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: 6,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >
            {submitting && <Spinner />}
            {submitting
              ? "Submitting…"
              : hasMissing
              ? "Submit Anyway"
              : "Submit Exam"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeBanner({ practiceMode, chairMode, devMode }) {
  let config;
  if (practiceMode) {
    config = {
        label: "PRACTICE MODE",
        bg: "#e3f2fd",
        fg: "#0d47a1",
        sub: "This attempt does not count toward your official score."
    };
  }
  if (chairMode) {
    config = {
        label: "CHAIR VIEW",
        bg: "#4a148c",
        fg: "#fff",
        sub: "You may complete and submit the exam to receive feedback. Submissions are not saved."
    };
  }
  if (devMode) {
    config = {
        label: "DEVELOPMENT MODE – NOT A REAL EXAM",
        bg: "#E60000",
        fg: "#fff",
        sub: ""
    };
  }

  return (
    <div
      style={{
        background: config.bg,
        color: config.fg,
        padding: "12px 16px",
        borderRadius: 8,
        marginBottom: 24,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}
    >
      <div>
        <strong style={{ fontSize: 14, letterSpacing: 0.5 }}>
          {config.label}
        </strong>
        <div style={{ fontSize: 13, opacity: 0.9 }}>
          {config.sub}
        </div>
      </div>
    </div>
  );
}

